import ExcelJS from 'exceljs';
import type { TestOrder, PurchaseInvoice } from '../types';

const CERTIFICATE_TEMPLATE_URL = '/Certificate of work completion.xlsx';
const TABLE_START_ROW = 15; // вставка данных с 15-й строки Excel (1-based), заголовки не вставляем
const TABLE_COLUMNS = 16;

const FONT_CALIBRI_9 = { name: 'Calibri', size: 9 };
const BORDER_THIN = { style: 'thin' as const };
const ALL_BORDERS = {
  top: BORDER_THIN,
  left: BORDER_THIN,
  bottom: BORDER_THIN,
  right: BORDER_THIN,
};
/** Выравнивание по вертикали по середине + перенос текста */
const ALIGN_MIDDLE_WRAP = { vertical: 'middle' as const, wrapText: true };
/** Rp с группировкой по 3 разряда: Rp 2 855 174.00 (до миллиардов) */
const NUM_FMT_IDR = '"Rp "# ### ### ### ###.00';
/** Количество: всегда показывать целую часть (0,00 а не ,00) — без разделителя разрядов */
const NUM_FMT_QTY = '0.00';
/** Без округления (проброс значения) */
function toFloat2(n: number): number {
  return n;
}

/** Формат даты "DD Mon YYYY" (например 15 Feb 2026) */
function formatDateDDMonYYYY(d: Date): string {
  const day = d.getDate().toString().padStart(2, '0');
  const mon = d.toLocaleDateString('en-GB', { month: 'short' });
  const year = d.getFullYear();
  return `${day} ${mon} ${year}`;
}

/** Формат даты "DD Month YYYY" (например 15 February 2026) */
function formatDateDDMonthYYYY(d: Date): string {
  const day = d.getDate().toString().padStart(2, '0');
  const mon = d.toLocaleDateString('en-GB', { month: 'long' });
  const year = d.getFullYear();
  return `${day} ${mon} ${year}`;
}

/** Фиксированная ширина столбцов: 9,10,12,14,16 = 15; 7,11,13,15 = 10 */
const COL_WIDTH_SUM = 15;   // price, orderAmount, acceptedPreviouslySum, remainingToAcceptSum, acceptedNowSum
const COL_WIDTH_QTY = 10;   // orderQty, acceptedPreviously, remainingToAccept, acceptedNow

/** Из комментария оставляем только строки Down payment, Retention, PPH и Inc VAT */
function filterCommentToDPRetentionPPH(comment: string): string {
  if (!comment) return '';
  return comment.split('\n').filter((line) => {
    const t = line.trim();
    return t.startsWith('- Down payment') || t.startsWith('- Retention') || t.startsWith('Exc PPH') || t.startsWith('Inc VAT');
  }).join('\n');
}

export type CertificateRow = {
  lineNo: number;
  contractNumber: string;
  characteristic: string;
  nameRu: string;
  nameEn: string;
  content: string;
  orderQty: number;
  unit: string;
  price: number;
  orderAmount: number;
  acceptedPreviously: number;
  acceptedPreviouslySum: number;
  remainingToAccept: number;
  remainingToAcceptSum: number;
  acceptedNow: number;
  acceptedNowSum: number;
};

/**
 * Строит строки таблицы сертификата по заказу и текущим значениям "Qty to accept".
 */
export function buildCertificateRows(
  order: TestOrder,
  editableAcceptQty: Record<string, string>
): CertificateRow[] {
  const contractNumber = order.Contract_Number ?? '';
  const parseQty = (val: string | undefined) =>
    parseFloat(String(val || '0').replace(',', '.')) || 0;
  return (order.Запасы ?? []).map((item, idx) => {
    const orderQty = Number(item.Количество) || 0;
    const orderAmount = Number(item.Сумма) || 0;
    const unitPrice = orderQty !== 0 ? orderAmount / orderQty : 0;
    const receivedQty =
      order.Invoices?.reduce((acc, inv) => {
        const m = inv.Items?.find(
          (invItem) =>
            invItem.Номенклатура_Key === item.Номенклатура_Key &&
            (invItem.Содержание || '') === (item.Содержание || '')
        );
        return acc + (Number(m?.Количество) || 0);
      }, 0) ?? 0;
    const remaining = Math.max(0, orderQty - receivedQty);
    const acceptedPreviously = orderQty - remaining;
    const rowKey = `${order.Ref_Key}_${idx}`;
    const effectiveQty = parseQty(editableAcceptQty[rowKey]);
    return {
      lineNo: idx + 1,
      contractNumber,
      characteristic: item.Characteristic_Name ?? '',
      nameRu: item.Nomenclature_Name ?? '',
      nameEn: item.Nomenclature_Eng ?? '',
      content: item.Содержание ?? '',
      orderQty,
      unit: item.Unit_Name ?? '',
      price: unitPrice,
      orderAmount,
      acceptedPreviously,
      acceptedPreviouslySum: unitPrice * acceptedPreviously,
      remainingToAccept: remaining,
      remainingToAcceptSum: unitPrice * remaining,
      acceptedNow: effectiveQty,
      acceptedNowSum: unitPrice * effectiveQty,
    };
  });
}

/**
 * Строит строки таблицы сертификата по конкретному прогрессу (приходной накладной).
 * Количество, позиции и суммы берутся из накладной.
 */
export function buildCertificateRowsForInvoice(
  order: TestOrder,
  invoice: PurchaseInvoice,
  invoicesBefore: PurchaseInvoice[]
): CertificateRow[] {
  const contractNumber = order.Contract_Number ?? '';
  return (order.Запасы ?? []).map((item, idx) => {
    const orderQty = Number(item.Количество) || 0;
    const orderAmount = Number(item.Сумма) || 0;
    const unitPrice = orderQty !== 0 ? orderAmount / orderQty : 0;
    const receivedBefore = invoicesBefore.reduce((acc, inv) => {
      const m = inv.Items?.find(
        (invItem) =>
          invItem.Номенклатура_Key === item.Номенклатура_Key &&
          (invItem.Содержание || '') === (item.Содержание || '')
      );
      return acc + (Number(m?.Количество) || 0);
    }, 0);
    const match = invoice.Items?.find(
      (invItem) =>
        invItem.Номенклатура_Key === item.Номенклатура_Key &&
        (invItem.Содержание || '') === (item.Содержание || '')
    );
    const acceptedNow = match ? Number(match.Количество) || 0 : 0;
    const acceptedPreviously = receivedBefore;
    const totalReceived = receivedBefore + acceptedNow;
    const remainingToAccept = Math.max(0, orderQty - totalReceived);
    return {
      lineNo: idx + 1,
      contractNumber,
      characteristic: item.Characteristic_Name ?? '',
      nameRu: item.Nomenclature_Name ?? '',
      nameEn: item.Nomenclature_Eng ?? '',
      content: item.Содержание ?? '',
      orderQty,
      unit: item.Unit_Name ?? '',
      price: unitPrice,
      orderAmount,
      acceptedPreviously,
      acceptedPreviouslySum: unitPrice * acceptedPreviously,
      remainingToAccept,
      remainingToAcceptSum: unitPrice * remainingToAccept,
      acceptedNow,
      acceptedNowSum: unitPrice * acceptedNow,
    };
  });
}

export type FillCertificateOptions = {
  /** Наименование юр. лица организации — Sheet1 C5, Defect_List C6 */
  organizationLegalName?: string;
  /** Наименование контрагента — подставляется в ячейку C8 (Sheet1) и Defect_List C9 */
  counterpartyName?: string;
  /** Номер договора — для замены ContractNum в строках 13 (Sheet1) и 3 (Defect_List) */
  contractNumber?: string;
  /** Дата договора — для замены ContractDate в строках 13 (Sheet1) и 3 (Defect_List), формат "DD Month YYYY" */
  contractDate?: Date | string;
  /** Дата сертификата — для ячейки P3 (DD Mon YYYY); если не задано, используется текущая дата */
  certificateDate?: Date | string;
  /** Таблица взаиморасчетов под основной таблицей */
  settlements?: {
    contractNumber: string;
    orderAmount: number;       // Order amount, IDR
    amountPaid: number;        // Amount paid under the contract, IDR
    remaining: number;         // Remaining amount under the contract, IDR
    totalAmountAccept: number; // Сумма к оплате за текущий прогресс после вычета DP и Retention
    invoiceComment?: string;   // Комментарий приходной накладной (предоплата, удержание)
  };
};

/**
 * Загружает шаблон, вставляет только данные таблицы с 15-й строки (без заголовков), сдвигая всё ниже вниз.
 * Шрифт Calibri 9, границы, выравнивание по вертикали по середине.
 * P3- екущая дата (DD Mon YYYY), C8 — наименование контрагента (если передано). Возвращает blob для скачивания.
 */
export async function fillCertificateTemplate(
  rows: CertificateRow[],
  options: FillCertificateOptions = {}
): Promise<Blob> {
  const res = await fetch(CERTIFICATE_TEMPLATE_URL);
  if (!res.ok) {
    throw new Error(
      `Шаблон сертификата не найден (${res.status}). Положите файл "Certificate of work completion.xlsx" в папку public.`
    );
  }
  const arrayBuffer = await res.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);
  const sheet = workbook.worksheets[0];
  if (!sheet) {
    throw new Error('В файле шаблона нет листов.');
  }

  const certDate = options.certificateDate != null
    ? (typeof options.certificateDate === 'string' ? new Date(options.certificateDate) : options.certificateDate)
    : new Date();
  const currentDateStr = formatDateDDMonYYYY(isNaN(certDate.getTime()) ? new Date() : certDate);
  // P3 — дата сертификата (DD Mon YYYY)
  sheet.getCell('P3').value = currentDateStr; 
  // C5 — наименование юр. лица организации
  if (options.organizationLegalName != null && options.organizationLegalName !== '') {
    sheet.getCell('C5').value = options.organizationLegalName;
  }
  // C8 — наименование контрагента
  if (options.counterpartyName != null && options.counterpartyName !== '') {
    sheet.getCell('C8').value = options.counterpartyName;
  }

  // Строка 13 (Sheet1): замена переменных ContractNum и ContractDate в тексте
  const row13 = sheet.getRow(13);
  // Ищем ячейки в строке 13, которые могут содержать текст с переменными
  // Проверяем все ячейки в строке, включая объединенные
  for (let col = 1; col <= 15; col++) {
    const cell = row13.getCell(col);
    let cellValue = cell.value;
    // Преобразуем значение в строку, если это не строка (может быть формула или число)
    if (cellValue != null) {
      const cellValueStr = typeof cellValue === 'string' ? cellValue : String(cellValue);
      let newValue = cellValueStr;
      let changed = false;
      
      // Заменяем ContractNum на номер договора
      if (options.contractNumber && cellValueStr.includes('ContractNum')) {
        newValue = newValue.replace(/ContractNum/g, options.contractNumber);
        changed = true;
      }
      
      // Заменяем ContractDate на дату договора
      if (options.contractDate && cellValueStr.includes('ContractDate')) {
        try {
          const contractDateObj = typeof options.contractDate === 'string' 
            ? new Date(options.contractDate) 
            : options.contractDate;
          if (!isNaN(contractDateObj.getTime())) {
            const contractDateStr = formatDateDDMonthYYYY(contractDateObj);
            newValue = newValue.replace(/ContractDate/g, contractDateStr);
            changed = true;
          }
        } catch (e) {
          console.warn('Failed to parse contract date:', options.contractDate, e);
        }
      }
      
      if (changed && newValue !== cellValueStr) {
        cell.value = newValue;
      }
    }
  }

  // Второй лист "Defect_List": C6 — юр. лицо, C9 — контрагент, H4 — дата (тот же формат)
  const defectSheet = workbook.getWorksheet('Defect_List');
  if (defectSheet) {
    if (options.organizationLegalName != null && options.organizationLegalName !== '') {
      defectSheet.getCell('C6').value = options.organizationLegalName;
    }
    if (options.counterpartyName != null && options.counterpartyName !== '') {
      defectSheet.getCell('C9').value = options.counterpartyName;
    }
    defectSheet.getCell('H4').value = currentDateStr;

    // Строка 2 (Defect_List): замена CertDate на текущую дату в тексте
    const defectRow2 = defectSheet.getRow(2);
    for (let col = 1; col <= 15; col++) {
      const cell = defectRow2.getCell(col);
      const cellValue = cell.value;
      if (cellValue != null) {
        const cellValueStr = typeof cellValue === 'string' ? cellValue : String(cellValue);
        if (cellValueStr.includes('CertDate')) {
          cell.value = cellValueStr.replace(/CertDate/g, currentDateStr);
        }
      }
    }

    // Строка 3 (Defect_List): замена переменных ContractNum и ContractDate в тексте
    const defectRow3 = defectSheet.getRow(3);
    for (let col = 1; col <= 15; col++) {
      const cell = defectRow3.getCell(col);
      let cellValue = cell.value;
      // Преобразуем значение в строку, если это не строка (может быть формула или число)
      if (cellValue != null) {
        const cellValueStr = typeof cellValue === 'string' ? cellValue : String(cellValue);
        let newValue = cellValueStr;
        let changed = false;
        
        // Заменяем ContractNum на номер договора
        if (options.contractNumber && cellValueStr.includes('ContractNum')) {
          newValue = newValue.replace(/ContractNum/g, options.contractNumber);
          changed = true;
        }
        
        // Заменяем ContractDate на дату договора
        if (options.contractDate && cellValueStr.includes('ContractDate')) {
          try {
            const contractDateObj = typeof options.contractDate === 'string' 
              ? new Date(options.contractDate) 
              : options.contractDate;
            if (!isNaN(contractDateObj.getTime())) {
              const contractDateStr = formatDateDDMonthYYYY(contractDateObj);
              newValue = newValue.replace(/ContractDate/g, contractDateStr);
              changed = true;
            }
          } catch (e) {
            console.warn('Failed to parse contract date:', options.contractDate, e);
          }
        }
        
        if (changed && newValue !== cellValueStr) {
          cell.value = newValue;
        }
      }
    }
  }

  // Порядок колонок: lineNo, contractNumber, characteristic, nameRu, nameEn, content, orderQty, unit, price, orderAmount, acceptedPreviously, acceptedPreviouslySum, remainingToAccept, remainingToAcceptSum, acceptedNow, acceptedNowSum
  const dataRows: (string | number)[][] = rows.map((row) => [
    toFloat2(row.lineNo),
    row.contractNumber,
    row.characteristic,
    row.nameRu,
    row.nameEn,
    row.content,
    toFloat2(row.orderQty),
    row.unit,
    toFloat2(row.price),
    toFloat2(row.orderAmount),
    toFloat2(row.acceptedPreviously),
    toFloat2(row.acceptedPreviouslySum),
    toFloat2(row.remainingToAccept),
    toFloat2(row.remainingToAcceptSum),
    toFloat2(row.acceptedNow),
    toFloat2(row.acceptedNowSum),
  ]);

  // Итоги по столбцам сумм: 10 (orderAmount), 12 (acceptedPreviouslySum), 14 (remainingToAcceptSum), 16 (acceptedNowSum)
  const SUM_COLS = [10, 12, 14, 16];
  const totalsRow: (string | number)[] = [];
  for (let c = 1; c <= TABLE_COLUMNS; c++) {
    if (SUM_COLS.includes(c)) {
      const sum = rows.reduce((acc, row) => {
        const val = [row.orderAmount, row.acceptedPreviouslySum, row.remainingToAcceptSum, row.acceptedNowSum][SUM_COLS.indexOf(c)];
        return acc + (val || 0);
      }, 0);
      totalsRow.push(toFloat2(sum));
    } else {
      totalsRow.push('');
    }
  }

  // Вставляем строки данных + строка итогов в позицию 15 (заголовки не вставляем)
  const allRows = [...dataRows, totalsRow];
  if (dataRows.length > 0) {
    sheet.spliceRows(TABLE_START_ROW, 0, ...allRows);

    const ROW_HEIGHT_WRAP = 30;
    const IDR_COLS = [9, 10, 12, 14, 16]; // price, orderAmount, acceptedPreviouslySum, remainingToAcceptSum, acceptedNowSum
    const QTY_COLS = [7, 11, 13, 15]; // orderQty, acceptedPreviously, remainingToAccept, acceptedNow

    for (let r = 0; r < allRows.length; r++) {
      const excelRow = sheet.getRow(TABLE_START_ROW + r);
      excelRow.height = ROW_HEIGHT_WRAP;
      const rowData = allRows[r];
      const isTotalsRow = r === dataRows.length;
      for (let c = 1; c <= TABLE_COLUMNS; c++) {
        const cell = excelRow.getCell(c);
        const raw = rowData[c - 1];
        if (typeof raw === 'number') {
          cell.value = toFloat2(raw);
        } else if (raw === '' || raw == null) {
          cell.value = '';
        } else {
          cell.value = raw;
        }
        if (IDR_COLS.includes(c) && (typeof raw === 'number' || (isTotalsRow && SUM_COLS.includes(c) && raw !== ''))) {
          cell.numFmt = NUM_FMT_IDR;
        } else if (QTY_COLS.includes(c) && typeof raw === 'number') {
          cell.numFmt = NUM_FMT_QTY;
        }
        cell.font = isTotalsRow ? { ...FONT_CALIBRI_9, bold: true } : FONT_CALIBRI_9;
        (cell as any).border = isTotalsRow ? undefined : ALL_BORDERS;
        cell.alignment = ALIGN_MIDDLE_WRAP;
      }
    }
    // Фиксированная ширина столбцов
    const WIDTH_15 = [9, 10, 12, 14, 16];
    const WIDTH_10 = [11, 13, 15];
    for (const c of WIDTH_15) sheet.getColumn(c).width = COL_WIDTH_SUM;
    for (const c of WIDTH_10) sheet.getColumn(c).width = COL_WIDTH_QTY;
    sheet.getColumn(7).width = 15; // Колонка G — фиксированная ширина 15 (комментарий в таблице взаиморасчётов)
  }

  const insertedRowCount = dataRows.length > 0 ? dataRows.length + 1 : 0; // +1 для строки итогов
  const nextRow = TABLE_START_ROW + insertedRowCount;
  const TITLE_ROW = nextRow + 1;           // строка с надписью "Current settlements..." (в шаблоне)
  const SETTLE_HEADER_ROW = nextRow + 2;    // строка заголовков таблицы взаиморасчетов (в шаблоне, после надписи)
  const SETTLE_DATA_ROW = nextRow + 3;     // строка данных взаиморасчетов
  const SETTLE_EMPTY_ROW = nextRow + 4;    // пустая строка после таблицы взаиморасчетов
  const SETTLE_COLUMNS = 7;                // A–G, последний — комментарий накладной
  // G и H–J — без границ в строках между таблицами и в строке данных взаиморасчётов (G в SETTLE_DATA_ROW потом получит границы при заполнении)
  const COLS_GHIJ = [7, 8, 9, 10];
  const MAX_COL = 15;

  const clearBorder = (rowIdx: number, col: number) => {
    const cell = sheet.getRow(rowIdx).getCell(col);
    try {
      if (cell.isMerged) {
        const master = cell.master;
        if (master) {
          (master as any).border = undefined;
        }
      }
    } catch {
      // игнор
    }
    // Полное удаление границ — undefined лучше убирает границы шаблона, чем пустой объект
    (cell as any).border = undefined;
  };

  // Пустая строка и надпись "Current settlements..." — без границ (заголовок таблицы SETTLE_HEADER_ROW не трогаем)
  for (const rowIdx of [nextRow, TITLE_ROW]) {
    for (let col = 1; col <= MAX_COL; col++) {
      clearBorder(rowIdx, col);
    }
  }
  // G, H, I, J — очистка для nextRow, TITLE_ROW и строки данных взаиморасчётов (SETTLE_DATA_ROW.G потом получит границы при заполнении)
  for (const rowIdx of [nextRow, TITLE_ROW, SETTLE_DATA_ROW]) {
    for (const col of COLS_GHIJ) clearBorder(rowIdx, col);
  }
  // Таблица "Взаиморасчеты": границы только в одной строке данных и только в столбцах A:F
  // У строки данных взаиморасчетов убираем границы в столбцах G и далее
  for (let col = SETTLE_COLUMNS + 1; col <= MAX_COL; col++) {
    clearBorder(SETTLE_DATA_ROW, col);
  }
  // Пустая строка после таблицы взаиморасчетов — без границ (только эта строка, остальные данные шаблона не трогаем)
  for (let col = 1; col <= MAX_COL; col++) {
    clearBorder(SETTLE_EMPTY_ROW, col);
  }

  if (options.settlements) {
    const s = options.settlements;
    const dataCells: (string | number)[] = [
      1,
      s.contractNumber,
      s.orderAmount,
      s.amountPaid,
      s.remaining,
      s.totalAmountAccept,
      filterCommentToDPRetentionPPH(s.invoiceComment ?? ''),
    ];
    for (let c = 1; c <= SETTLE_COLUMNS; c++) {
      const cell = sheet.getRow(SETTLE_DATA_ROW).getCell(c);
      cell.value = dataCells[c - 1];
      cell.font = FONT_CALIBRI_9;
      cell.border = ALL_BORDERS;
      cell.alignment = ALIGN_MIDDLE_WRAP;
      if (c >= 3 && c <= 6) {
        cell.numFmt = NUM_FMT_IDR;
      }
    }
  }

  // Область печати: первый лист — от A1 до строки, следующей за последними данными на листе (rowCount + 1)
  sheet.pageSetup = sheet.pageSetup || {};
  const lastPrintRow = sheet.rowCount > 0 ? sheet.rowCount + 1 : SETTLE_EMPTY_ROW;
  sheet.pageSetup.printArea = `A1:P${lastPrintRow}`;
  if (defectSheet) {
    defectSheet.pageSetup = defectSheet.pageSetup || {};
    const defectLastPrintRow = defectSheet.rowCount > 0 ? defectSheet.rowCount + 1 : 25;
    defectSheet.pageSetup.printArea = `A1:H${defectLastPrintRow}`;
  }

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/**
 * Предлагает пользователю сохранить файл. Если доступен File System Access API (Chrome, Edge),
 * открывается диалог выбора места сохранения; иначе — скачивание через ссылку.
 */
export async function saveBlobWithPicker(blob: Blob, suggestedName: string, mimeType: string): Promise<void> {
  const types = mimeType === 'application/pdf'
    ? [{ description: 'PDF', accept: { 'application/pdf': ['.pdf'] } }]
    : [{ description: 'Excel', accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] } }];
  try {
    if ('showSaveFilePicker' in window) {
      const handle = await (window as any).showSaveFilePicker({ suggestedName, types });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    }
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') return;
    console.warn('showSaveFilePicker failed, falling back to download', e);
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = suggestedName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
